<div class="container">

  <header class="page-heading">
    <h1>FD Calculator</h1>
    <p>Calculate the maturity value and interest earned on a bank Fixed Deposit (FD), compounded quarterly, with this online calculator.</p>
  </header>

  <div class="card mb-lg">
    <div class="section-title">Fixed Deposit Details</div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('principal')">
        <label class="field-label" for="principal">Deposit Amount</label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="principal" type="number"
            [(ngModel)]="principal"
            [min]="C.principal.min" [max]="C.principal.max" [step]="C.principal.step"
            (ngModelChange)="onFieldChange('principal')"
            (blur)="onFieldBlur('principal')"
            placeholder="e.g. 1,00,000"
            autocomplete="off"/>
        </div>
        <div class="field-hint">{{ C.principal.min | currency:'INR':'symbol':'1.0-0':'en-IN' }} – {{ C.principal.max | currency:'INR':'symbol':'1.0-0':'en-IN' }}</div>
        @if (hasError('principal')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('principal') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('annualRate')">
        <label class="field-label" for="annualRate">Interest Rate</label>
        <div class="input-shell suffix">
          <input id="annualRate" type="number"
            [(ngModel)]="annualRate"
            [min]="C.rate.min" [max]="C.rate.max" [step]="C.rate.step"
            (ngModelChange)="onFieldChange('annualRate')"
            (blur)="onFieldBlur('annualRate')"
            placeholder="e.g. 7"
            autocomplete="off"/>
          <span class="affix right">%</span>
        </div>
        @if (hasError('annualRate')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('annualRate') }}
          </div>
        }
      </div>
    </div>

    <div class="field-group" [class.has-error]="hasError('tenureYears')">
      <label class="field-label" for="tenureYears">Tenure</label>
      <div class="input-shell suffix">
        <input id="tenureYears" type="number"
          [(ngModel)]="tenureYears"
          [min]="C.tenureYears.min" [max]="C.tenureYears.max" [step]="C.tenureYears.step"
          (ngModelChange)="onFieldChange('tenureYears')"
          (blur)="onFieldBlur('tenureYears')"
          placeholder="e.g. 5"
          autocomplete="off"/>
        <span class="affix right">Yr</span>
      </div>
      <div class="field-hint">Interest is compounded quarterly, the standard for Indian bank FDs.</div>
      @if (hasError('tenureYears')) {
        <div class="field-error">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {{ fieldError('tenureYears') }}
        </div>
      }
    </div>

    <div class="action-row">
      <button type="button" class="btn btn-primary" (click)="calculate()" [class.btn-disabled]="showFormErrors && !isFormValid">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        Calculate
      </button>
      @if (calculated) {
        <button type="button" class="btn btn-secondary" (click)="reset()">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
          Reset
        </button>
      }
      @if (showFormErrors && !isFormValid) {
        <span class="form-error-hint">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Fix the errors above to calculate
        </span>
      }
    </div>
  </div>

  @if (calculated && result) {
    <div class="summary-grid mb-md">
      <div class="summary-card c-blue">
        <div class="sc-icon"></div>
        <div class="sc-label">Maturity Value</div>
        <div class="sc-value">{{ fmt.currency(result.maturityValue) }}</div>
        <div class="sc-sub">after {{ tenureYears }} years</div>
      </div>
      <div class="summary-card c-green">
        <div class="sc-icon"></div>
        <div class="sc-label">Deposit Amount</div>
        <div class="sc-value">{{ fmt.currency(result.principal) }}</div>
        <div class="sc-sub">one-time</div>
      </div>
      <div class="summary-card c-red">
        <div class="sc-icon"></div>
        <div class="sc-label">Interest Earned</div>
        <div class="sc-value">{{ fmt.currency(result.totalInterest) }}</div>
        <div class="sc-sub">@ {{ annualRate }}% p.a., compounded quarterly</div>
      </div>
    </div>

    <div class="card charts-card mb-md">
      <div class="section-title">Visual Breakdown</div>
      <div class="charts-top-row">
        <div class="chart-panel">
          <div class="chart-panel-label">Deposit vs Interest</div>
          <div class="chart-canvas-wrap chart-canvas-pie">
            <canvas #breakdownRef></canvas>
          </div>
        </div>
        <div class="chart-panel chart-panel-bar">
          <div class="chart-panel-label">Balance Growth</div>
          <div class="chart-canvas-wrap">
            <canvas #growthRef></canvas>
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-md">
      <div class="section-title">Quarter-wise Growth</div>
      <div class="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th class="t-left">Quarter</th>
              <th class="t-left">Year</th>
              <th class="t-balance">Balance</th>
            </tr>
          </thead>
          <tbody>
            @for (row of result.quarters; track row.quarter) {
              <tr>
                <td class="t-left t-num">Q{{ row.quarter }}</td>
                <td class="t-left">Year {{ row.year }}</td>
                <td class="t-balance">{{ fmt.currency(row.balance) }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  }
</div>

<!-- ══ SEO CONTENT ══════════════════════════════════════════════════════ -->
<section class="container seo-content">
  <div class="card">
    <h2 class="section-title">How is FD Maturity Value Calculated?</h2>
    <p>
      A Fixed Deposit (FD) locks in a one-time deposit for a fixed tenure at a fixed interest rate.
      Indian banks typically compound FD interest quarterly.
    </p>
    <p class="emi-formula">A = P &times; (1 + r/4)<sup>4t</sup></p>
    <ul class="emi-formula-vars">
      <li><strong>P</strong> &mdash; Principal (deposit amount)</li>
      <li><strong>r</strong> &mdash; Annual interest rate</li>
      <li><strong>t</strong> &mdash; Tenure in years</li>
    </ul>
    <p>
      For example, depositing &#8377;1,00,000 for 5 years at 7% p.a., compounded quarterly, matures
      to about &#8377;1.42 lakh.
    </p>

    <h2 class="section-title mt-sm">Frequently Asked Questions</h2>
    <div class="faq-item">
      <h3>Is FD interest taxable?</h3>
      <p>
        Yes. Interest earned on a bank FD is fully taxable as per your income tax slab, and banks
        deduct TDS if the interest earned in a year exceeds the prescribed threshold.
      </p>
    </div>
    <div class="faq-item">
      <h3>Can I withdraw an FD before maturity?</h3>
      <p>
        Most banks allow premature withdrawal, usually with a penalty of 0.5&ndash;1% on the
        applicable interest rate. Check your bank's specific terms before booking the FD.
      </p>
    </div>
    <p class="learn-crosslink">
      Read more: <a routerLink="/learn/fd-vs-rd-vs-ppf">FD vs RD vs PPF: where should each rupee go?</a>
    </p>
  </div>
</section>
